import React from "react";
import Icon from "../Icon";
import Panel from "../Panel";
import ProgressBar from "../ProgressBar";
import Toast from "../Toast";
import TodaysPlanAnnouncement from "./TodaysPlanAnnouncement";
import TodaysPlanCompleteBanner from "./TodaysPlanCompleteBanner";
import { DailyLimitModal, InfoModal } from "./TodaysPlanModals";
import TodaysPlanPanel from "./TodaysPlanPanel";
import useTodaysPlan from "./useTodaysPlan";

function PlanHeader({ plan }) {
  return (
    <>
      <div className="tw-flex tw-items-center tw-gap-3">
        <Icon name="trophy" style={{ color: "#f4b73b", fontSize: 22 }} type="fa" />
        <span className="joystix tw-flex-1" style={{ fontSize: 18 }}>Today's Plan</span>
        <button aria-label="About Today's Plan" className="btn btn-link" data-testid="todays-plan-info-button" onClick={plan.openInfo}>
          <Icon name="info-circle" type="fa" />
        </button>
      </div>
      <small className="text-secondary">A little bit closer to fluency every day</small>
    </>
  );
}

// Holds the card's place while the saved plan loads, and offers a retry if it can't, rather than guessing a plan.
function PlanPlaceholderCard({ plan }) {
  return (
    <div data-testid="todays-plan-placeholder">
      <Panel>
        <div className="tw-flex tw-flex-col tw-gap-3">
          <PlanHeader plan={plan} />
          <ProgressBar percent={0} style={{ marginBottom: 0 }} />
          <small className="text-secondary" style={{ visibility: plan.isLoadFailed ? "visible" : "hidden" }}>Today's Plan couldn't load.</small>
          {plan.isLoadFailed ? (
            <button className="btn btn-default btn-lg joystix" data-testid="todays-plan-retry-button" onClick={plan.retryLoad}>Try again</button>
          ) : (
            <button className="btn btn-success btn-lg joystix" disabled>Play</button>
          )}
        </div>
      </Panel>
      {plan.isInfoVisible && <InfoModal onHidden={plan.closeInfo} />}
    </div>
  );
}

function PlanCard({ availability, collections, defaultMaxReviewsPerCollectionPerDay, isPro, onOpenReviewSettings, plan }) {
  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <PlanHeader plan={plan} />
      <ProgressBar percent={plan.percent} style={{ marginBottom: 0 }} />
      <small className="text-secondary" data-testid="todays-plan-numerical-progress"><strong>{plan.completedItems}</strong>/{plan.totalItems} played</small>
      <div className="tw-flex tw-items-center tw-gap-3">
        <a
          aria-disabled={!plan.playUrl}
          className={`btn btn-success btn-lg joystix tw-flex-1 ${plan.playUrl ? "" : "disabled"}`}
          data-testid="todays-plan-play-button"
          href={plan.playUrl || undefined}
          onClick={plan.playUrl ? plan.startPlaying : undefined}
        >
          Play
        </a>
        <button
          aria-label={plan.isPanelExpanded ? "Hide plan" : "Show plan"}
          className={`btn btn-default btn-lg todays-plan-icon-btn tw-self-stretch ${plan.isPanelExpanded ? "active" : ""}`}
          data-testid="todays-plan-summary-button"
          onClick={plan.togglePanel}
        >
          <Icon name={plan.isPanelExpanded ? "chevron-up" : "chevron-down"} type="fa" />
        </button>
      </div>
      {plan.isPanelExpanded && (
        <TodaysPlanPanel
          availability={availability}
          collections={collections}
          config={plan.config}
          defaultMaxReviewsPerCollectionPerDay={defaultMaxReviewsPerCollectionPerDay}
          isPro={isPro}
          onConfigChange={plan.changeConfig}
          onOpenReviewSettings={onOpenReviewSettings}
          queue={plan.queue}
        />
      )}
    </div>
  );
}

function TodaysPlanCard({
  collections,
  defaultMaxReviewsPerCollectionPerDay,
  isPro,
  isSpeakingAvailable,
  isSystemTtsAvailable,
  isTranslateReviewsEnabled,
  onOpenReviewSettings,
  plan
}) {
  return (
    <div data-testid="todays-plan">
      <Panel>
        {plan.showCompleteBanner ? (
          <TodaysPlanCompleteBanner
            completedItems={plan.completedItems}
            freeCardsRemaining={plan.freeCardsRemaining}
            hasMoreNewWords={plan.hasMoreNewWords}
            isPro={isPro}
            onAddMoreWords={plan.addMoreNewWords}
            onDismiss={plan.dismissBanner}
            totalItems={plan.totalItems}
            untappedReviews={plan.untappedReviews}
          />
        ) : (
          <PlanCard
            availability={{ isSpeakingAvailable, isSystemTtsAvailable, isTranslateReviewsEnabled }}
            collections={collections}
            defaultMaxReviewsPerCollectionPerDay={defaultMaxReviewsPerCollectionPerDay}
            isPro={isPro}
            onOpenReviewSettings={onOpenReviewSettings}
            plan={plan}
          />
        )}
      </Panel>
      {plan.isInfoVisible && <InfoModal onHidden={plan.closeInfo} />}
      {plan.isDailyLimitVisible && <DailyLimitModal onHidden={plan.closeDailyLimit} />}
    </div>
  );
}

export default function TodaysPlan(props) {
  const { accountCreatedAt, isAnnouncementBlocked, onPlanStatusChange, username } = props;
  const plan = useTodaysPlan(props);

  React.useEffect(() => {
    if(plan.isLoaded && onPlanStatusChange) onPlanStatusChange({ isComplete: plan.isComplete, totalItems: plan.totalItems });
  }, [plan.isLoaded, plan.isComplete, plan.totalItems]);

  const isCardHidden = plan.isLoaded && plan.isComplete && !plan.showCompleteBanner;

  return (
    <>
      {!plan.isLoaded && <PlanPlaceholderCard plan={plan} />}
      {plan.isLoaded && !isCardHidden && <TodaysPlanCard plan={plan} {...props} />}
      <TodaysPlanAnnouncement accountCreatedAt={accountCreatedAt} isBlocked={isAnnouncementBlocked} username={username} />
      {plan.saveFailure && (
        <Toast content="Your plan changes could not be saved. Please try again." context="danger" key={plan.saveFailure.id} maxWidth={320} />
      )}
    </>
  );
}
